import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { confirmTournamentCashPayment, removeTournamentParticipant, ApiError } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { formatLocal } from '../../lib/time';
import { fullName } from '../../lib/format';
import { feeGelFromMinor, participantStatusColor, participantStatusLabel } from '../../lib/tournaments';
import type { AdminParticipant, TtarParticipantEnrollmentState } from '../../lib/types';

interface Props {
  tournamentId: number;
  feeMinor: number;
  participants: AdminParticipant[];
  loading: boolean;
  onChanged: () => void;
  /** Undefined until the TTAR state request has completed. */
  enrollments?: TtarParticipantEnrollmentState[];
  retryingTtar?: boolean;
  onRetryTtar?: () => void;
}

/**
 * R5 roster: identity, participant/payment state, removal. Deliberately no
 * refund status, promise, or completion action anywhere — tournament refunds
 * are entirely outside ProSpin (epic-wide decision, 2026-08-23). Removing a
 * participant here only frees their spot; whatever happens to money already
 * paid is a conversation the club has with the player outside this admin
 * panel.
 */
export function TournamentRoster({
  tournamentId,
  feeMinor,
  participants,
  loading,
  onChanged,
  enrollments,
  retryingTtar = false,
  onRetryTtar,
}: Props) {
  const { push } = useToast();
  const [removing, setRemoving] = useState<AdminParticipant | null>(null);
  const [busy, setBusy] = useState(false);
  const enrollmentByUser = new Map(enrollments?.map((enrollment) => [enrollment.user_id, enrollment]));
  const showTtar = enrollments !== undefined;

  // Irreversible money action (there is no un-confirm endpoint), so it goes
  // behind the same ConfirmDialog pattern as removal — which also absorbs the
  // double-click window.
  const [confirmingCash, setConfirmingCash] = useState<AdminParticipant | null>(null);
  const [cashBusy, setCashBusy] = useState(false);
  const handleConfirmCash = async () => {
    if (!confirmingCash) return;
    setCashBusy(true);
    try {
      await confirmTournamentCashPayment(tournamentId, confirmingCash.user_id);
      push(
        'success',
        confirmingCash.fee_due
          ? `Marked paid — ${fullName(confirmingCash.users)}'s fee is settled.`
          : `Marked paid — ${fullName(confirmingCash.users)} is confirmed.`,
      );
      setConfirmingCash(null);
      onChanged();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not confirm the cash payment');
      setConfirmingCash(null);
    } finally {
      setCashBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await removeTournamentParticipant(tournamentId, removing.user_id);
      push('success', `Removed ${fullName(removing.users)} from the roster.`);
      setRemoving(null);
      onChanged();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Removal failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ ...T.caption, color: PS.textSec }}>Loading roster…</div>;
  }
  if (participants.length === 0) {
    return <div style={{ ...T.caption, color: PS.textSec }}>No participants yet.</div>;
  }

  return (
    <>
      <div
        style={{
          background: PS.white,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
              <th style={th}>Participant</th>
              <th style={th}>Status</th>
              <th style={th}>Payment</th>
              <th style={th}>Registered</th>
              {showTtar && <th style={th}>TTAR</th>}
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                <td style={td}>
                  <Link to={`/users/${p.user_id}`} style={{ color: PS.linkBlue, textDecoration: 'none' }}>
                    {fullName(p.users)}
                  </Link>
                  <div style={{ ...T.caption, color: PS.textSec }}>
                    {p.users.phone ?? (p.users.telegram_id ? `tg:${p.users.telegram_id}` : '—')}
                  </div>
                </td>
                <td style={{ ...td, color: participantStatusColor(p.status) }}>
                  {participantStatusLabel(p.status)}
                </td>
                <td style={td}>
                  {p.fee_due ? (
                    <>
                      <div style={{ color: PS.pendingText }}>Not paid</div>
                      {p.payment && p.payment.status !== 'confirmed' && (
                        <>
                          ₾{p.payment.amount} · {p.payment.method}
                          <div style={{ ...T.caption, color: PS.textSec }}>{p.payment.status}</div>
                        </>
                      )}
                    </>
                  ) : p.payment ? (
                    <>
                      ₾{p.payment.amount} · {p.payment.method}
                      <div style={{ ...T.caption, color: PS.textSec }}>{p.payment.status}</div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={td}>{formatLocal(p.registered_at, 'yyyy-MM-dd HH:mm')}</td>
                {showTtar && (
                  <td style={td}>
                    <TtarEnrollment
                      enrollment={enrollmentByUser.get(p.user_id)}
                      feeSettled={!p.fee_due && p.payment?.status === 'confirmed'}
                      retrying={retryingTtar}
                      onRetry={onRetryTtar}
                    />
                  </td>
                )}
                <td style={td}>
                  {((p.status === 'pending_payment' &&
                    p.payment?.method === 'cash' &&
                    p.payment.status === 'pending_payment') ||
                    (p.status === 'confirmed' && p.fee_due)) && (
                      <Button onClick={() => setConfirmingCash(p)}>
                        Mark paid (cash)
                      </Button>
                    )}{' '}
                  <Button variant="danger" onClick={() => setRemoving(p)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmingCash !== null}
        title="Mark paid (cash)?"
        message={
          confirmingCash
            ? confirmingCash.fee_due
              ? `Received ₾${feeGelFromMinor(feeMinor)} in cash from ${fullName(confirmingCash.users)}? This records the fee as paid and cannot be undone.`
              : `Received ₾${confirmingCash.payment?.amount} in cash from ${fullName(confirmingCash.users)}? This confirms their seat and cannot be undone.`
            : ''
        }
        confirmLabel={cashBusy ? 'Confirming…' : 'Yes, received'}
        onConfirm={handleConfirmCash}
        onCancel={() => { if (!cashBusy) setConfirmingCash(null); }}
      />

      <ConfirmDialog
        open={removing !== null}
        title="Remove participant?"
        message={
          removing
            ? `Remove ${fullName(removing.users)} from the roster and free their spot. This does not touch any payment record. This cannot be undone.`
            : ''
        }
        confirmLabel={busy ? 'Removing…' : 'Remove'}
        destructive
        onConfirm={handleRemove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

function TtarEnrollment({
  enrollment,
  feeSettled,
  retrying,
  onRetry,
}: {
  enrollment: TtarParticipantEnrollmentState | undefined;
  feeSettled: boolean;
  retrying: boolean;
  onRetry: (() => void) | undefined;
}) {
  if (!enrollment) {
    return <span style={{ ...T.caption, color: PS.textSec }}>Not queued</span>;
  }

  const paidMarkPending = enrollment.status === 'enrolled' && feeSettled && !enrollment.paid_marked_at;
  const retryAvailable = enrollment.status === 'pending' || enrollment.status === 'failed' || paidMarkPending;
  const { label, color } = enrollmentPresentation(enrollment.status);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
      <span style={{ ...T.microLabel, color }}>{label}</span>
      {enrollment.remote_participant_id != null && (
        <span style={{ ...T.caption, color: PS.textSec }}>#{enrollment.remote_participant_id}</span>
      )}
      {enrollment.status === 'enrolled' && (
        enrollment.paid_marked_at ? (
          <span style={{ ...T.caption, color: PS.success }}>Paid on TTAR</span>
        ) : feeSettled ? (
          <>
            <span style={{ ...T.caption, color: PS.pendingText }}>Paid mark pending</span>
            {enrollment.paid_mark_error && (
              <span style={{ ...T.caption, color: PS.errorText, overflowWrap: 'anywhere' }}>
                {enrollment.paid_mark_error}
              </span>
            )}
          </>
        ) : null
      )}
      {enrollment.last_error && (
        <span style={{ ...T.caption, color: PS.errorText, overflowWrap: 'anywhere' }}>{enrollment.last_error}</span>
      )}
      {enrollment.last_attempt_at && (
        <span style={{ ...T.caption, color: PS.textSec }}>
          Attempt {enrollment.attempts} · {formatLocal(enrollment.last_attempt_at, 'yyyy-MM-dd HH:mm')}
        </span>
      )}
      {enrollment.status === 'retry_exhausted' && (
        <span style={{ ...T.caption, color: PS.textSec }}>Retry limit reached</span>
      )}
      {retryAvailable && onRetry && (
        <Button variant="secondary" disabled={retrying} onClick={onRetry}>
          {retrying ? 'Retrying…' : 'Retry TTAR sync'}
        </Button>
      )}
    </div>
  );
}

function enrollmentPresentation(status: TtarParticipantEnrollmentState['status']) {
  switch (status) {
    case 'enrolled':
      return { label: 'Enrolled', color: PS.success };
    case 'failed':
      return { label: 'Failed', color: PS.danger };
    case 'retry_exhausted':
      return { label: 'Retry limit reached', color: PS.danger };
    case 'enrolling':
      return { label: 'Enrolling', color: PS.info };
    case 'pending':
    default:
      return { label: 'Pending', color: PS.pendingText };
  }
}

const th: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontFamily: "'Barlow Condensed',sans-serif",
  fontSize: 12,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: PS.textSec,
};

const td: CSSProperties = {
  padding: '10px 12px',
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  color: PS.nearBlack,
};
